import React from 'react';
import { Link } from 'react-router-dom';
import { ToolSectionRenderer } from '../tools/ToolPageSections';
import ScheduleCalendar from './ScheduleCalendar';
import StreamCustomTablesSection from './StreamCustomTablesSection';
import { renderSetupListItem } from './scheduleUtils';

const GENERIC_TYPES = new Set(['description', 'video', 'faq', 'cta', 'steps', 'platformVideos']);

export function StreamSectionRenderer({ section, schedule = [] }) {
  switch (section.type) {
    case 'scheduleCalendar':
      return <ScheduleCalendar section={section} schedule={schedule} />;

    case 'customTables':
      return <StreamCustomTablesSection section={section} />;

    case 'aboutStream':
      return (
        <div className="stream-about-block">
          <div className="platform-comparison">
            <h3>
              🎬 <span className="highlight">YouTube</span> -{' '}
              {section.youtubeHeading?.replace('YouTube - ', '') || 'Pure Gameplay Experience'}
            </h3>
            {(section.youtubeParagraphs || []).map((p, i) => (
              <p key={i}>
                {p.startsWith('Note:') ? <b>Note: </b> : null}
                {p.startsWith('Note:') ? p.slice(6) : p}
              </p>
            ))}

            <h3>
              🎮 <span className="highlight">Twitch</span> -{' '}
              {section.twitchHeading?.replace('Twitch - ', '') || 'Interactive Fun'}
            </h3>
            <p>{section.twitchParagraph || ''}</p>
          </div>

          <h3>{section.expectHeading || 'What to Expect:'}</h3>
          <ul>
            {(section.expectItems || []).map((item, i) => (
              <li key={i}>
                <strong>{item.label}</strong> {item.text}
                {item.link && (
                  <>
                    {' '}
                    You can find them in the{' '}
                    <Link to={item.link}>
                      <span className="highlight">{item.linkLabel}</span>
                    </Link>
                    .
                  </>
                )}
              </li>
            ))}
          </ul>
          {section.supportText && <p>{section.supportText}</p>}
        </div>
      );

    case 'features':
      if (!(section.features || []).length) return null;
      return (
        <div className="stream-features">
          <h2 className="features-title">
            {section.heading || 'Stream Features &'}
            <span className="highlight">{section.highlight || ' Subscriber Benefits'}</span>
          </h2>
          <div className="features-grid">
            {(section.features || []).map((feature) => (
              <div className="feature-card" key={feature.title}>
                <div className="feature-header">
                  <i
                    className={`${feature.icon} feature-icon`}
                    style={{ color: feature.iconColor }}
                    aria-hidden="true"
                  />
                  <h3>{feature.title}</h3>
                </div>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'setup':
      if (!(section.setupCategories || []).length) return null;
      return (
        <>
          {section.intro && <p>{section.intro}</p>}
          <div className="stream-setup">
            <div className="setup-grid">
              {(section.setupCategories || []).map((category) => (
                <div className="setup-category" key={category.name}>
                  <h3>
                    {category.emoji} <span className="highlight">{category.name}</span>
                  </h3>
                  <ul>
                    {(category.items || []).map((item) => renderSetupListItem(item))}
                  </ul>
                </div>
              ))}

              {(section.pcSpecs || []).length > 0 && (
                <div className="setup-category dual-pc-full-width">
                  <h3>
                    🖥️ <span className="highlight">{section.triplePcHeading || 'Triple PC Setup'}</span>
                  </h3>
                  <div className="triple-pc-setup">
                    {section.pcSpecs.map((pc) => (
                      <div className="pc-specs" key={pc.name}>
                        <h4>{pc.emoji} {pc.name}</h4>
                        <ul>
                          {(pc.specs || []).map((spec) => (
                            <li key={spec.label}>
                              <strong>{spec.label}:</strong> {spec.value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  {(section.streamingCommunication || []).length > 0 && (
                    <div className="obs-communication">
                      <h4>🔗 {section.streamingCommunicationHeading || 'Streaming Communication'}</h4>
                      {(section.streamingCommunication || []).map((entry) => (
                        <p key={entry.label}>
                          <strong>{entry.label}:</strong> {entry.text}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      );

    default:
      if (GENERIC_TYPES.has(section.type)) {
        return <ToolSectionRenderer section={section} />;
      }
      return null;
  }
}
